<script setup>
import { computed, onMounted, ref } from "vue";
import { Download } from "lucide-vue-next";
import { RouterLink } from "vue-router";
import { API_BASE, APP_BASE, BRAND_LOGO_URL, MAC_APP_DOWNLOAD_URL, TOTAL_TOKEN_MILESTONES } from "@/shared/config/constants";
import { compact } from "@/shared/utils/format";

const avatarBase = `${APP_BASE}/landing/avatars`;
const featuredUserName = "howie.serious";

const valueCards = [
  {
    num: "01",
    title: "一次安装，自动上传",
    body: "装好之后就不用再管了。你在 Claude Code、Codex 里烧的每一个 token，它都在菜单栏里默默记账、按天汇总、自动上传。",
  },
  {
    num: "02",
    title: "熟人排行，真的有效",
    body: "榜上都是你认识的人。看见同伴今天又烧了多少，比任何打卡提醒都管用，游戏化在社区里才真正跑得起来。",
  },
  {
    num: "03",
    title: "目标清晰，进度可见",
    body: "每天 1 亿，全年 1000 亿。这是衡量「行动落地」最简单也最硬的标准，距离目标还有多远，打开就知道。",
  },
];

const leaderboardRows = ref([]);
const leaderboardLoaded = ref(false);
const leaderboardError = ref(false);
const communityRandomAvatars = ref([]);
const personalTotal = ref(null);
const finalMilestone = TOTAL_TOKEN_MILESTONES[TOTAL_TOKEN_MILESTONES.length - 1];

const featuredUsage = computed(() => personalTotal.value ?? leaderboardRows.value.find((row) => row.user?.displayName === featuredUserName) ?? leaderboardRows.value[0] ?? null);
const featuredTokens = computed(() => featuredUsage.value?.totalTokens ?? null);
const featuredMilestone = computed(() => TOTAL_TOKEN_MILESTONES.find((item) => (featuredTokens.value ?? 0) < item.value) ?? finalMilestone);
const featuredPct = computed(() => {
  const target = featuredMilestone.value?.value ?? finalMilestone.value;
  return featuredTokens.value == null ? 0 : (featuredTokens.value / target) * 100;
});
const isPreviewLoading = computed(() => !leaderboardLoaded.value && !leaderboardError.value);
const featuredPctText = computed(() => (isPreviewLoading.value ? "读取中" : `${featuredPct.value.toFixed(1)}%`));
const featuredBarWidth = computed(() => `${Math.min(Math.max(featuredPct.value, 0), 100).toFixed(2)}%`);
const featuredTokenText = computed(() => (isPreviewLoading.value ? "读取中" : compact.format(featuredTokens.value ?? 0)));
const featuredYearPct = computed(() => featuredPct.value);
const featuredYearBarWidth = computed(() => `${Math.min(Math.max(featuredYearPct.value, 0), 100).toFixed(2)}%`);
const featuredYearLabel = computed(() => (personalTotal.value ? "我的总量里程碑" : "社区总量里程碑"));
const featuredYearValue = computed(() => {
  if (leaderboardError.value) return "暂时不可用";
  if (!leaderboardLoaded.value) return "读取中";
  if (featuredTokens.value == null) return "等待已上传总量";
  return `${featuredMilestone.value?.label ?? "1000 亿"} · ${featuredYearPct.value.toFixed(1)}%`;
});
const featuredGoalCopy = computed(() => {
  if (leaderboardError.value) return "实时数据暂时不可用。稍后刷新后会重新读取已上传总量榜。";
  if (!leaderboardLoaded.value) return "正在读取社区已上传总量榜。";
  if (!featuredUsage.value) return "还没有已上传总量记录，完成同步后这里会显示总量进度。";
  const target = featuredMilestone.value;
  if (!target || featuredTokens.value >= target.value) return "1000 亿长期目标已达成。";
  return `距离 ${target.label} 还差 ${compact.format(target.value - featuredTokens.value)}。`;
});
const nextUserRow = {
  rank: "4",
  name: "下一个是你",
  tokens: "-",
  initial: "你",
  isYou: true,
};
const previewRows = computed(() => {
  if (!leaderboardLoaded.value || leaderboardError.value) return [];
  const liveRows = leaderboardRows.value.slice(0, 3).map((row) => {
    const name = row.user?.displayName || `成员 ${row.rank}`;
    return {
      rank: String(row.rank),
      name,
      tokens: compact.format(row.totalTokens),
      avatarUrl: row.user?.avatarUrl || null,
      initial: name.slice(0, 1),
      isFeatured: name === featuredUserName,
      highlight: name === featuredUserName,
    };
  });
  return [...liveRows, nextUserRow];
});

const fixedCommunityAvatars = [
  { type: "image", key: "howie", src: "howie-256.png", label: "howie.serious" },
  { type: "image", key: "azen", src: "azen.png", label: "阿禅" },
];
const communityAvatars = computed(() => [...fixedCommunityAvatars, ...communityRandomAvatars.value]);

function sampleCommunityAvatars(rows) {
  const fixedNames = new Set([featuredUserName, "阿禅"]);
  const seen = new Set();
  const candidates = [];
  for (const row of rows) {
    const name = row.user?.displayName?.trim();
    if (!name || fixedNames.has(name) || seen.has(name)) continue;
    seen.add(name);
    candidates.push({
      type: "user",
      key: `user-${row.user?.id || name}`,
      label: name,
      avatarUrl: row.user?.avatarUrl || null,
      initial: name.slice(0, 1),
    });
  }
  for (let i = candidates.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
  }
  return candidates.slice(0, 3);
}

async function fetchLeaderboardData(path) {
  const response = await fetch(path, {
    credentials: "include",
    headers: { accept: "application/json" },
  });
  if (!response.ok) throw Object.assign(new Error(`leaderboard ${response.status}`), { status: response.status });
  return response.json();
}

async function loadPreviewLeaderboard() {
  try {
    leaderboardError.value = false;
    const data = await fetchLeaderboardData(`${API_BASE}/public/leaderboard/total?provider=all&limit=100`);
    try {
      const auth = await fetchLeaderboardData(`${API_BASE}/auth/me`);
      if (auth.user) {
        const mine = await fetchLeaderboardData(`${API_BASE}/me/total?provider=all`);
        personalTotal.value = mine.item ?? null;
      } else {
        personalTotal.value = null;
      }
    } catch {
      personalTotal.value = null;
    }
    leaderboardRows.value = Array.isArray(data.items) ? data.items : [];
    communityRandomAvatars.value = sampleCommunityAvatars(leaderboardRows.value);
  } catch {
    leaderboardRows.value = [];
    communityRandomAvatars.value = [];
    personalTotal.value = null;
    leaderboardError.value = true;
  } finally {
    leaderboardLoaded.value = true;
  }
}

onMounted(() => {
  loadPreviewLeaderboard();
});

const steps = [
  {
    num: "1",
    title: "下载并打开",
    body: "下载 dmg，拖进「应用程序」，它会安静地待在你的菜单栏里。",
  },
  {
    num: "2",
    title: "配对设备",
    body: "在菜单栏里完成一次设备配对，把这台 mac 和你的社区身份绑定。",
  },
  {
    num: "3",
    title: "每天使用 agent！",
    body: "照常干活就行。用量按天自动汇总上传，打开排行榜看今天的进度。",
    inverse: true,
  },
];

const faqs = [
  {
    question: "会上传我的对话或代码吗？",
    answer: "不会。只上传 token 用量的按日汇总（输入、输出、缓存和成本估算），不读取、不上传任何对话内容或代码。",
  },
  {
    question: "支持哪些平台和 agent？",
    answer: "目前是 mac 菜单栏 app，支持 Claude Code 和 Codex 的本地用量记录自动统计。",
  },
  {
    question: "为什么目标是每天 1 亿 token？",
    answer: "拥抱 AI 范式，最重要的是行动落地，而不是口头表达。token 消耗量是衡量行动最简单、最硬的指标，每天 1 亿，一年 1000 亿，进度实时可见。",
  },
  {
    question: "我不在社区，能用吗？",
    answer: "可以直接下载使用。但排行榜的乐趣在于榜上都是熟人，加入做中学 · ai 社群，和大家一起烧，效果最好。",
  },
  {
    question: "需要一直开着它吗？",
    answer: "它就是一个安静的菜单栏图标，开机自启、按刷新频率自动上传，不需要任何日常操作。",
  },
];
</script>

<template>
  <main class="about-page">
    <header class="about-topbar" data-screen-label="顶栏">
      <RouterLink class="about-brand" :to="{ name: 'leaderboard' }">
        <img :src="BRAND_LOGO_URL" alt="做中学熊" />
        <span>小能熊 · token 排行榜</span>
      </RouterLink>
      <nav class="about-nav" aria-label="About 页面导航">
        <RouterLink class="about-nav-link" :to="{ name: 'leaderboard' }">排行榜</RouterLink>
        <a class="about-download-small" :href="MAC_APP_DOWNLOAD_URL">下载客户端</a>
      </nav>
    </header>

    <section class="about-hero" data-screen-label="Hero">
      <div class="about-narrow">
        <div class="about-pills">
          <span class="about-pill-solid">做中学 · ai 社群 出品</span>
          <span class="about-pill-outline">mac 菜单栏 app</span>
        </div>
        <h1>每天 1 亿 token</h1>
        <p>
          2026，最重要的事情只有一件：全面转向 agent 范式！<br />
          一次安装，自动统计你在 Codex 和 Claude Code 里的 token 消耗。<br />
          在小能熊社区，和熊友们一起走进 agent 新时代！
        </p>
        <div class="about-hero-actions">
          <a class="about-btn about-btn-black about-btn-large" :href="MAC_APP_DOWNLOAD_URL">
            <Download :size="18" :stroke-width="2.6" />
            下载 mac 客户端
          </a>
          <RouterLink class="about-btn about-btn-ghost about-btn-large" :to="{ name: 'leaderboard' }">先看排行榜</RouterLink>
        </div>
        <small>一次配对 · 自动上传</small>
      </div>
    </section>

    <section class="about-section about-paper about-bordered" data-screen-label="三大价值点">
      <div class="about-wide">
        <div class="about-value-grid">
          <article v-for="card in valueCards" :key="card.num" class="about-value-card">
            <span>{{ card.num }}</span>
            <h3>{{ card.title }}</h3>
            <p>{{ card.body }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="about-preview about-paper" data-screen-label="产品预览">
      <div class="about-wide">
        <div class="about-section-head">
          <h2>已上传总量榜</h2>
        </div>
        <div class="about-preview-grid">
          <article class="about-goal-card">
            <div class="about-goal-label">{{ personalTotal ? "我的" : "社区" }} · 已上传总量</div>
            <div class="about-goal-number">
              <strong>{{ featuredTokenText }}</strong>
              <span>/ {{ featuredMilestone?.label || "1000 亿" }} token</span>
            </div>
            <div class="about-progress" :aria-label="`已上传总量进度 ${featuredPctText}`">
              <span :style="{ width: featuredBarWidth }"></span>
            </div>
            <div class="about-goal-year">
              <span>{{ featuredYearLabel }}</span>
              <strong>{{ featuredYearValue }}</strong>
            </div>
            <div class="about-progress about-progress-thin" :aria-label="`总量里程碑完成度 ${featuredYearPct.toFixed(1)}%`">
              <span :style="{ width: featuredYearBarWidth }"></span>
            </div>
            <p>{{ featuredGoalCopy }}</p>
          </article>

          <article class="about-rank-card">
            <header>
              <span>总量排行</span>
              <span>total tokens</span>
            </header>
            <div v-if="previewRows.length === 0" class="about-rank-empty">
              {{ leaderboardError ? "实时榜单暂时不可用" : "正在读取已上传总量榜" }}
            </div>
            <div v-for="row in previewRows" :key="row.rank" class="about-rank-row" :class="{ highlight: row.highlight }">
              <span class="about-rank-num">{{ row.rank }}</span>
              <img v-if="row.avatarUrl" :src="row.avatarUrl" alt="" />
              <img v-else-if="row.avatar" :src="`${avatarBase}/${row.avatar}`" alt="" />
              <span v-else class="about-avatar-you" :class="{ featured: row.isFeatured || row.isYou }">{{ row.initial }}</span>
              <strong>{{ row.name }}</strong>
              <em>{{ row.tokens }}</em>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="about-steps" data-screen-label="安装三步">
      <div class="about-wide">
        <h2>三步上手，之后全自动</h2>
        <div class="about-step-grid">
          <article v-for="step in steps" :key="step.num" class="about-step">
            <span :class="{ inverse: step.inverse }">{{ step.num }}</span>
            <div>
              <h3>{{ step.title }}</h3>
              <p>{{ step.body }}</p>
            </div>
          </article>
        </div>
        <div class="about-agents">
          <span>支持的 agents</span>
          <strong>claude code</strong>
          <strong>codex</strong>
        </div>
      </div>
    </section>

    <section class="about-community" data-screen-label="社区">
      <div class="about-narrow">
        <div class="about-avatar-stack" aria-hidden="true">
          <template v-for="avatar in communityAvatars" :key="avatar.key">
            <img v-if="avatar.type === 'image'" :src="`${avatarBase}/${avatar.src}`" alt="" :title="avatar.label" />
            <img v-else-if="avatar.avatarUrl" :src="avatar.avatarUrl" alt="" :title="avatar.label" />
            <span v-else :title="avatar.label">{{ avatar.initial }}</span>
          </template>
          <span>你?</span>
        </div>
        <h2>学 ai，做中学</h2>
        <p>
          实战 · 共学 · 长期主义。<br />
          和熟悉的人一起，把「拥抱 AI」从一句口号，变成每天看得见的 token 数。
        </p>
      </div>
    </section>

    <section class="about-section about-paper about-faq" data-screen-label="FAQ">
      <div class="about-faq-inner">
        <h2>FAQ</h2>
        <div class="about-faq-list">
          <details v-for="faq in faqs" :key="faq.question">
            <summary>{{ faq.question }}</summary>
            <p>{{ faq.answer }}</p>
          </details>
        </div>
      </div>
    </section>

    <section class="about-bottom-cta" data-screen-label="底部CTA">
      <div class="about-narrow">
        <img :src="BRAND_LOGO_URL" alt="做中学熊" />
        <h2>每天一亿 token，今天就开始！</h2>
        <p>2026，最重要的事只有 1 件：全面拥抱 agent 范式。</p>
        <a class="about-btn about-btn-black about-btn-xl" :href="MAC_APP_DOWNLOAD_URL">
          <Download :size="18" :stroke-width="2.6" />
          下载 mac 客户端
        </a>
        <small>apple silicon · 自动更新 · 免费</small>
      </div>
    </section>

    <footer class="about-footer" data-screen-label="页脚">
      <img :src="BRAND_LOGO_URL" alt="" />
      <span>学 AI，做中学 · 做中学 · AI 社群</span>
      <RouterLink :to="{ name: 'leaderboard' }">排行榜</RouterLink>
    </footer>
  </main>
</template>

<style scoped>
.about-page {
  min-height: 100vh;
  background: var(--amber);
  color: var(--ink);
  font-family: var(--font-sans);
}

.about-topbar {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px clamp(16px, 4vw, 40px);
  border-bottom: 3px solid var(--ink);
  background: var(--amber);
}

.about-brand,
.about-nav {
  display: flex;
  align-items: center;
}

.about-brand {
  gap: 12px;
  min-width: 0;
  color: var(--ink);
  text-decoration: none;
}

.about-brand img {
  height: 36px;
  flex: 0 0 auto;
}

.about-brand span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 17px;
  font-weight: 900;
}

.about-nav {
  gap: 22px;
  flex: 0 0 auto;
}

.about-nav-link {
  color: var(--ink);
  font-size: 15px;
  font-weight: 700;
  text-decoration: none;
}

.about-download-small {
  display: inline-flex;
  align-items: center;
  min-height: 38px;
  padding: 0 20px;
  border-radius: 999px;
  background: var(--ink);
  color: var(--amber);
  font-size: 14px;
  font-weight: 900;
  text-decoration: none;
  white-space: nowrap;
  transition: background 120ms ease;
}

.about-download-small:hover,
.about-btn-black:hover {
  background: var(--ink-2);
}

.about-hero,
.about-bottom-cta {
  background: var(--amber);
  text-align: center;
}

.about-hero {
  padding: 78px clamp(20px, 5vw, 48px) 74px;
}

.about-narrow,
.about-wide,
.about-faq-inner {
  width: 100%;
  margin: 0 auto;
}

.about-narrow {
  max-width: 880px;
}

.about-wide {
  max-width: 1160px;
}

.about-hero .about-narrow,
.about-bottom-cta .about-narrow,
.about-community .about-narrow {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.about-pills,
.about-hero-actions,
.about-section-head,
.about-agents,
.about-footer {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.about-pills {
  justify-content: center;
  gap: 10px;
}

.about-pill-solid,
.about-pill-outline {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 800;
}

.about-pill-solid {
  background: var(--ink);
  color: var(--amber);
}

.about-pill-outline {
  box-shadow: inset 0 0 0 2px var(--ink);
  font-family: var(--font-mono);
}

.about-hero h1,
.about-section h2,
.about-preview h2,
.about-steps h2,
.about-community h2,
.about-bottom-cta h2 {
  font-weight: 900;
  line-height: 1.05;
  letter-spacing: 0;
}

.about-hero h1 {
  margin: 26px 0 0;
  font-size: 116px;
  line-height: 1;
  text-transform: lowercase;
  text-wrap: balance;
}

.about-hero p {
  max-width: 680px;
  margin: 20px auto 0;
  font-size: 22px;
  font-weight: 200;
  line-height: 1.6;
  text-wrap: pretty;
}

.about-hero-actions {
  justify-content: center;
  gap: 14px;
  margin-top: 36px;
}

.about-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 999px;
  font-weight: 900;
  text-decoration: none;
  transition:
    background 120ms ease,
    transform 120ms ease;
}

.about-btn-large {
  min-height: 56px;
  padding: 0 34px;
  font-size: 17px;
}

.about-btn-xl {
  min-height: 60px;
  padding: 0 40px;
  font-size: 18px;
}

.about-btn-black {
  background: var(--ink);
  color: var(--amber);
}

.about-btn-ghost {
  box-shadow: inset 0 0 0 2px var(--ink);
  background: var(--amber);
  color: var(--ink);
}

.about-btn-ghost::after {
  content: "→";
  font-weight: 900;
}

.about-btn-ghost:hover {
  background: var(--amber-deep);
}

.about-hero small,
.about-bottom-cta small {
  margin-top: 16px;
  color: var(--ink-3);
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 700;
}

.about-paper {
  background: var(--paper);
}

.about-bordered {
  border-top: 3px solid var(--ink);
}

.about-section,
.about-faq {
  padding: 72px clamp(20px, 5vw, 48px);
}

.about-section h2,
.about-preview h2,
.about-steps h2,
.about-faq h2 {
  font-size: 44px;
}

.about-value-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
}

.about-value-card {
  border: 2px solid var(--ink);
  border-radius: 2px;
  background: #fff;
  box-shadow: 4px 4px 0 var(--ink);
  padding: 26px 24px;
  transition:
    transform 120ms ease,
    box-shadow 120ms ease;
}

.about-value-card:hover {
  transform: translate(-2px, -2px);
  box-shadow: 6px 6px 0 var(--ink);
}

.about-value-card span {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 12px;
  border: 2px solid var(--ink);
  border-radius: 999px;
  background: var(--amber);
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 900;
}

.about-value-card h3 {
  margin: 16px 0 0;
  font-size: 22px;
  font-weight: 900;
  letter-spacing: 0;
}

.about-value-card p,
.about-step p,
.about-faq p {
  color: var(--ink-2);
  font-size: 15px;
  font-weight: 200;
  line-height: 1.7;
}

.about-value-card p {
  margin: 10px 0 0;
}

.about-preview {
  padding: 0 clamp(20px, 5vw, 48px) 72px;
}

.about-section-head {
  align-items: baseline;
  gap: 12px;
}

.about-section-head span {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 0 12px;
  border-radius: 999px;
  background: var(--chip);
  color: var(--ink-4);
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 800;
}

.about-preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  align-items: start;
  gap: 20px;
  margin-top: 32px;
}

.about-goal-card {
  border-radius: 2px;
  background: var(--ink);
  box-shadow: 6px 6px 0 rgb(0 0 0 / 22%);
  padding: 32px;
}

.about-goal-label,
.about-rank-card header span {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.about-goal-label {
  color: var(--amber);
}

.about-goal-number {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
}

.about-goal-number strong {
  color: #fff;
  font-family: var(--font-mono);
  font-size: 48px;
  font-weight: 900;
  line-height: 1;
}

.about-goal-number span,
.about-goal-year span,
.about-goal-card p {
  color: var(--ink-5);
  font-weight: 200;
}

.about-goal-number span {
  font-size: 17px;
}

.about-progress {
  height: 20px;
  margin-top: 20px;
  overflow: hidden;
  border-radius: 2px;
  background: var(--ink-2);
}

.about-progress span {
  display: block;
  height: 100%;
  background: var(--amber);
}

.about-goal-year {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
}

.about-goal-year span {
  font-size: 13px;
}

.about-goal-year strong {
  color: var(--amber);
  font-family: var(--font-mono);
  font-size: 15px;
  font-weight: 900;
}

.about-progress-thin {
  height: 6px;
  margin-top: 8px;
}

.about-progress-thin span {
  background: var(--amber-deep);
}

.about-goal-card p {
  margin: 20px 0 0;
  font-size: 14px;
  line-height: 1.6;
}

.about-rank-card {
  overflow: hidden;
  border: 3px solid var(--ink);
  border-radius: 2px;
  background: #fff;
  box-shadow: 6px 6px 0 var(--ink);
}

.about-rank-card header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  background: var(--ink);
}

.about-rank-card header span:first-child {
  color: var(--amber);
}

.about-rank-card header span:last-child {
  color: #fff;
  font-family: var(--font-mono);
}

.about-rank-row {
  display: grid;
  grid-template-columns: 24px 36px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--line);
}

.about-rank-row:last-child {
  border-bottom: 0;
}

.about-rank-row.highlight {
  background: var(--amber-pale);
}

.about-rank-empty {
  padding: 24px 20px;
  color: var(--ink-4);
  font-size: 14px;
  font-weight: 800;
}

.about-rank-num,
.about-rank-row em {
  font-family: var(--font-mono);
  font-style: normal;
}

.about-rank-num {
  color: var(--ink);
  font-size: 18px;
  font-weight: 900;
}

.about-rank-row img,
.about-avatar-you,
.about-avatar-stack img,
.about-avatar-stack span {
  width: 36px;
  height: 36px;
  border: 2px solid var(--ink);
  border-radius: 999px;
  object-fit: cover;
}

.about-avatar-you,
.about-avatar-stack span {
  display: grid;
  place-items: center;
  background: var(--amber);
  font-weight: 900;
}

.about-avatar-you {
  background: var(--chip);
}

.about-avatar-you.featured {
  background: var(--amber);
}

.about-rank-row strong {
  min-width: 0;
  overflow: hidden;
  font-size: 15px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.about-rank-row em {
  color: var(--ink);
  font-size: 16px;
  font-weight: 900;
  white-space: nowrap;
}

.about-steps {
  border-top: 3px solid var(--ink);
  background: var(--amber);
  padding: 54px clamp(20px, 5vw, 48px);
}

.about-step-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 3px;
  margin-top: 26px;
  border: 3px solid var(--ink);
  background: var(--ink);
  box-shadow: 6px 6px 0 var(--ink);
}

.about-step {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background: #fff;
  padding: 20px 22px;
}

.about-step > span {
  display: grid;
  width: 44px;
  height: 44px;
  flex: 0 0 auto;
  place-items: center;
  border: 2px solid var(--ink);
  background: var(--amber);
  color: var(--ink);
  font-family: var(--font-mono);
  font-size: 20px;
  font-weight: 900;
}

.about-step > span.inverse {
  background: var(--ink);
  color: var(--amber);
}

.about-step h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 900;
  letter-spacing: 0;
}

.about-step p {
  margin: 6px 0 0;
}

.about-agents {
  gap: 12px;
  margin-top: 28px;
}

.about-agents span {
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.about-agents strong {
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: 0 18px;
  border-radius: 999px;
  background: var(--ink);
  color: var(--amber);
  font-family: var(--font-mono);
  font-size: 14px;
  font-weight: 800;
}

.about-community {
  background: var(--ink);
  padding: 78px clamp(20px, 5vw, 48px);
  text-align: center;
}

.about-avatar-stack {
  display: flex;
  align-items: center;
  justify-content: center;
}

.about-avatar-stack img,
.about-avatar-stack span {
  width: 64px;
  height: 64px;
  border: 3px solid var(--amber);
  background: var(--chip);
}

.about-avatar-stack img + img,
.about-avatar-stack img + span {
  margin-left: -16px;
}

.about-community h2 {
  margin: 32px 0 0;
  color: var(--amber);
  font-size: 64px;
  text-transform: lowercase;
}

.about-community p {
  max-width: 560px;
  margin: 18px auto 0;
  color: var(--line-soft);
  font-size: 18px;
  font-weight: 200;
  line-height: 1.7;
}

.about-faq-inner {
  max-width: 760px;
}

.about-faq-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 32px;
}

.about-faq details {
  border: 2px solid var(--ink);
  border-radius: 2px;
  background: #fff;
}

.about-faq summary {
  cursor: pointer;
  list-style: none;
  padding: 18px 22px;
  font-size: 16px;
  font-weight: 800;
}

.about-faq summary::-webkit-details-marker {
  display: none;
}

.about-faq summary:hover {
  background: var(--chip);
}

.about-faq p {
  margin: 0;
  padding: 0 22px 18px;
}

.about-bottom-cta {
  border-top: 3px solid var(--ink);
  padding: 82px clamp(20px, 5vw, 48px);
}

.about-bottom-cta img {
  height: 72px;
}

.about-bottom-cta h2 {
  margin: 24px 0 0;
  font-size: 78px;
  text-transform: lowercase;
  text-wrap: balance;
}

.about-bottom-cta p {
  margin: 16px 0 0;
  font-size: 20px;
  font-weight: 200;
  line-height: 1.6;
}

.about-bottom-cta .about-btn {
  margin-top: 32px;
}

.about-footer {
  justify-content: center;
  gap: 12px;
  border-top: 3px solid var(--ink);
  background: var(--paper);
  padding: 20px 24px;
}

.about-footer img {
  height: 26px;
  opacity: 0.75;
}

.about-footer span {
  color: var(--ink-3);
  font-size: 14px;
  font-weight: 200;
}

.about-footer a {
  color: var(--ink);
  font-size: 14px;
  font-weight: 700;
}

@media (max-width: 980px) {
  .about-hero h1 {
    font-size: 86px;
  }

  .about-bottom-cta h2 {
    font-size: 58px;
  }
}

@media (max-width: 759px) {
  .about-topbar {
    padding: 12px 16px;
  }

  .about-brand span {
    font-size: 15px;
  }

  .about-nav {
    gap: 10px;
  }

  .about-nav-link {
    display: none;
  }

  .about-download-small {
    min-height: 36px;
    padding: 0 14px;
    font-size: 13px;
  }

  .about-hero {
    padding: 46px 20px 52px;
  }

  .about-hero h1 {
    font-size: 58px;
  }

  .about-hero p {
    font-size: 17px;
  }

  .about-hero-actions {
    display: grid;
    grid-template-columns: 1fr;
    width: min(100%, 320px);
  }

  .about-btn-large,
  .about-btn-xl {
    width: 100%;
    min-height: 52px;
    padding: 0 22px;
    font-size: 16px;
  }

  .about-section,
  .about-faq {
    padding: 50px 20px;
  }

  .about-section h2,
  .about-preview h2,
  .about-steps h2,
  .about-faq h2 {
    font-size: 34px;
  }

  .about-preview {
    padding: 0 20px 50px;
  }

  .about-goal-card {
    padding: 24px;
  }

  .about-goal-number strong {
    font-size: 36px;
  }

  .about-rank-card header,
  .about-rank-row {
    padding-inline: 14px;
  }

  .about-rank-row {
    grid-template-columns: 22px 34px minmax(0, 1fr) auto;
    gap: 9px;
  }

  .about-rank-row img,
  .about-avatar-you {
    width: 34px;
    height: 34px;
  }

  .about-rank-row em {
    font-size: 14px;
  }

  .about-steps {
    padding: 46px 20px;
  }

  .about-step-grid {
    grid-template-columns: 1fr;
  }

  .about-community {
    padding: 58px 20px;
  }

  .about-avatar-stack img,
  .about-avatar-stack span {
    width: 54px;
    height: 54px;
  }

  .about-community h2 {
    font-size: 42px;
  }

  .about-bottom-cta {
    padding: 58px 20px;
  }

  .about-bottom-cta img {
    height: 64px;
  }

  .about-bottom-cta h2 {
    font-size: 42px;
  }
}
</style>
